/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { Icon, StyledModal, ModalActions, ButtonGroup, Button } from 'sarsaparilla';
import {
    calculateFees,
    formatCurrency,
    issuanceStatuses,
} from '../../utils/permitUtilsNew';
import * as types from '../../constants/index';
import Fees from '../Fees';

const propTypes = {
    permit: PropTypes.object.isRequired,
    division: PropTypes.object.isRequired,
    issuance: PropTypes.object.isRequired,
    groupMembers: PropTypes.arrayOf(PropTypes.object).isRequired,
    fees: PropTypes.array.isRequired,
    issuanceStatus: PropTypes.number.isRequired,
    permitCount: PropTypes.number,
};

const defaultProps = {
    groupMembers: [],
    fees: [],
    issuanceStatus: issuanceStatuses.get(types.PERMIT_IN_PROGRESS),
    permitCount: 1,
};

export default class FeeEstimate extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            feesVisible: false,
        };
        this.showFeesModal = this.showFeesModal.bind(this);
        this.closeFeesModal = this.closeFeesModal.bind(this);
    }

    showFeesModal() {
        this.setState({
            feesVisible: true,
        });
    }

    closeFeesModal() {
        this.setState({
            feesVisible: false,
        });
    }

    render() {
        const feesMap = calculateFees(
            this.props.division,
            this.props.fees,
            this.props.groupMembers,
            this.props.issuance
        );

        if (feesMap) {
            let totalCost = 0;

            for (const key in feesMap) {
                if (key in feesMap) {
                    const v = feesMap[key];
                    if (v.amount && !v.count) {
                        totalCost += v.amount * this.props.permitCount;
                    }
                }
            }

            let permitCountLabel = null;
            if (this.props.permitCount > 1) {
                permitCountLabel = <span>{this.props.permitCount} x </span>;
            }

            const amountPaid = this.props.issuance.amount_paid
                ? this.props.issuance.amount_paid
                : 0;
            const amountToPay = this.props.issuance?.fee_overrides?.reduce(
                (totalValue, item) => totalValue + item.amount,
                0
            );

            let adjustmentAmount = 0;
            if (
                this.props.issuance?.fee_overrides &&
                this.props.issuance?.fee_overrides.length > 0
            ) {
                adjustmentAmount = amountToPay - totalCost;
                totalCost = amountToPay;
            }

            return (
                <div className="fee">
                    {feesMap[types.ADULT_USE_FEES] && (
                        <div className="line-item">
                            <div className="amount-label">
                                {permitCountLabel}
                                {feesMap[types.ADULT_USE_FEES].count} x Adult:
                            </div>
                            <div className="amount">
                                {formatCurrency(
                                    feesMap[types.ADULT_USE_FEES].amount *
                                        this.props.permitCount
                                )}
                            </div>
                        </div>
                    )}
                    {feesMap[types.CHILD_USE_FEES] && (
                        <div className="line-item">
                            <div className="amount-label">
                                {permitCountLabel}
                                {feesMap[types.CHILD_USE_FEES].count} x Child:
                            </div>
                            <div className="amount">
                                {formatCurrency(
                                    feesMap[types.CHILD_USE_FEES].amount *
                                        this.props.permitCount
                                )}
                            </div>
                        </div>
                    )}
                    {feesMap[types.INTER_ACCESS_USE_FEES] && (
                        <div className="line-item">
                            <div className="amount-label">
                                {permitCountLabel}
                                {feesMap[types.INTER_ACCESS_USE_FEES].count} x Access
                                Pass:
                            </div>
                            <div className="amount">
                                {formatCurrency(
                                    feesMap[types.INTER_ACCESS_USE_FEES].amount *
                                        this.props.permitCount
                                )}
                            </div>
                        </div>
                    )}
                    {feesMap[types.INTER_SENIOR_USE_FEES] && (
                        <div className="line-item">
                            <div className="amount-label">
                                {permitCountLabel}
                                {feesMap[types.INTER_SENIOR_USE_FEES].count} x Senior
                                Pass:
                            </div>
                            <div className="amount">
                                {formatCurrency(
                                    feesMap[types.INTER_SENIOR_USE_FEES].amount *
                                        this.props.permitCount
                                )}
                            </div>
                        </div>
                    )}
                    {feesMap[types.INTER_YOUTH_USE_FEES] && (
                        <div className="line-item">
                            <div className="amount-label">
                                {permitCountLabel}
                                {feesMap[types.INTER_YOUTH_USE_FEES].count} x Youth Access
                                Pass:
                            </div>
                            <div className="amount">
                                {formatCurrency(
                                    feesMap[types.INTER_YOUTH_USE_FEES].amount *
                                        this.props.permitCount
                                )}
                            </div>
                        </div>
                    )}
                    {feesMap[types.SPH_USE_FEES] && (
                        <div className="line-item">
                            <div className="amount-label">
                                {permitCountLabel}
                                {feesMap[types.SPH_USE_FEES].count} x Season Card:
                            </div>
                            <div className="amount">
                                {formatCurrency(
                                    feesMap[types.SPH_USE_FEES].amount *
                                        this.props.permitCount
                                )}
                            </div>
                        </div>
                    )}
                    {feesMap[types.TCH_USE_FEES] && (
                        <div className="line-item">
                            <div className="amount-label">
                                {permitCountLabel}
                                {feesMap[types.TCH_USE_FEES].count} x Treaty Card:
                            </div>
                            <div className="amount">
                                {formatCurrency(
                                    feesMap[types.TCH_USE_FEES].amount *
                                        this.props.permitCount
                                )}
                            </div>
                        </div>
                    )}
                    {feesMap[types.MIN_BALANCE_FEE] && (
                        <div className="line-item">
                            <div className="amount-label">
                                {permitCountLabel}User Fee Deposit:
                            </div>
                            <div className="amount">
                                {formatCurrency(
                                    feesMap[types.MIN_BALANCE_FEE].amount *
                                        this.props.permitCount
                                )}
                            </div>
                        </div>
                    )}
                    <div className="line-item">
                        <div className="amount-label">
                            {permitCountLabel}Reservation Fee:
                        </div>
                        <div className="amount">
                            {formatCurrency(
                                feesMap[types.RESERVATION_FEE].amount *
                                    this.props.permitCount
                            )}
                        </div>
                    </div>
                    <br />
                    {this.props.issuance?.fee_overrides &&
                        this.props.issuance?.fee_overrides.length > 0 && (
                            <div className="line-item">
                                <div className="amount-label">
                                    {permitCountLabel}Fee Adjustment:
                                </div>
                                <div className="amount">
                                    {formatCurrency(adjustmentAmount)}
                                </div>
                            </div>
                        )}
                    <div className="line-item">
                        <div className="amount-label">{permitCountLabel}Amount Paid:</div>
                        <div className="amount">{formatCurrency(amountPaid)}</div>
                    </div>
                    <hr />
                    <div className="line-item">
                        <div className="amount-label">
                            <h2 className={'h5'}>Total Cost:</h2>
                            <button
                                className="rec-button-link"
                                onClick={this.showFeesModal}
                            >
                                <Icon iconName="info" />
                                <span className={'rec-sr-only'}>Show fee details</span>
                            </button>
                        </div>
                        <div className="amount">
                            <h3 className={'h5'}>
                                {formatCurrency(totalCost - amountPaid)}
                            </h3>
                        </div>
                    </div>
                    <StyledModal
                        shouldShowCloseButton={false}
                        shouldActionsStickInMobile
                        size="md"
                        isOpen={this.state.feesVisible}
                        onRequestClose={this.closeFeesModal}
                    >
                        <Fees permit={this.props.permit} />

                        <ModalActions>
                            <ButtonGroup isFullWidthOnMobile={false} isStretchedToFit>
                                <Button
                                    onClick={this.closeFeesModal}
                                    appearance="tertiary"
                                >
                                    Close
                                </Button>
                            </ButtonGroup>
                        </ModalActions>
                    </StyledModal>
                </div>
            );
        }
        return <div />;
    }
}

FeeEstimate.propTypes = propTypes;
FeeEstimate.defaultProps = defaultProps;
